import type { AvatarShape } from "@design-system/Avatar/Avatar";
import type {
    AnatomyBinding,
    AnatomyPart,
    AnatomyState,
    AnatomyVariant,
    ComponentAnatomySpec,
} from "./componentAnatomy";
import {
    avatarSeamBindings,
    type SizeBindings,
} from "./componentAnatomyAvatarSeams";
import {
    avatarAnatomyVariantId,
    avatarPaintBindings,
    avatarPaints,
} from "./componentAnatomyAvatarPaints";
import {
    avatarAnatomySizes,
    diamondOuterSide,
} from "./componentAnatomyAvatarSizes";
import { anatomyTextStyle } from "./componentAnatomyText";

const parts: readonly AnatomyPart[] = [
    {
        id: "background",
        label: "Root",
        element:
            ".avatar box: --avatar-size, --avatar-radius and --avatar-bg behind the photo",
    },
    {
        id: "fill",
        label: "Diamond",
        element:
            '[data-shape="diamond"]::before: a rotated square filling the outer diamond, behind the unrotated initial',
    },
    {
        id: "border",
        label: "Outline hairline",
        element:
            'inset box-shadow in --avatar-ring on [data-variant="outline"] (on ::before for diamonds)',
    },
    {
        id: "label",
        label: "Initial",
        element: ".fallback text, via --avatar-fg and the per-size text style",
    },
    {
        id: "glyph",
        label: "Fallback slot",
        element: "svg passed as `fallback` (the Kick logo), filling the box",
    },
    {
        id: "seam",
        label: "Group seam",
        element:
            "mask-image on each AvatarGroup .item that has a next neighbour: the next avatar's shape grown by the transparent seam",
    },
    {
        id: "countChip",
        label: "Overflow chip",
        element: "Tag numeric primary showing +N, after the stack",
    },
];

const states: readonly AnatomyState[] = [
    { id: "resting", label: "Default", selector: "" },
];

const sharedBindings: readonly AnatomyBinding[] = [
    {
        partId: "background",
        property: "background-color",
        token: "--color-ocean-blue-200",
        via: "--avatar-bg",
        note: "Through --avatar-color, which a `color` prop (a user's or entity's iconColor) replaces inline through styleWithVars.",
    },
    {
        partId: "label",
        property: "color",
        token: "--fg-primary",
        via: "--avatar-fg",
    },
    {
        partId: "glyph",
        property: "width / height",
        literal: "100%",
        note: "The fallback svg fills the avatar box.",
    },
];

/** Square and diamond avatars keep 10px letters at xs (spec B5). */
const withEntityLetter = (bindings: SizeBindings): SizeBindings => ({
    ...bindings,
    xs: [
        ...(bindings.xs ?? []),
        ...anatomyTextStyle({
            scale: "2xs",
            weight: "500",
            mixin: "text-2xs-10-medium",
            note: 'Re-declared by [data-size="xs"]:not([data-shape="circle"]), like EntityIcon and ClassIcon (spec B5).',
        }),
    ],
});

const radiusBinding = {
    partId: "background",
    property: "border-radius",
    via: "--avatar-radius",
} as const;

const diamondBindings: readonly AnatomyBinding[] = [
    {
        partId: "background",
        property: "background-color",
        literal: "transparent",
        note: "The root is only the layout box; the colour paints the rotated ::before.",
    },
    {
        partId: "fill",
        property: "outer side",
        literal: diamondOuterSide("var(--avatar-size)"),
        via: "--avatar-diamond-outer",
        note: "ds-deviation: Figma ClassIcon geometry, no token family. A --radius-xs square rotated 45deg whose rounded tips touch the box (13.66px at 16px), so it stays inside it.",
    },
    {
        partId: "fill",
        property: "outer border-radius",
        token: "--radius-xs",
        note: "The outer diamond's corner, as Figma's ClassIcon.",
    },
    {
        partId: "fill",
        property: "width / height",
        literal: "var(--avatar-diamond-outer)",
        note: "The drawn fill, centred with inset: 0 and margin: auto. It keeps its full size in stacks too.",
    },
    {
        partId: "fill",
        property: "transform",
        literal: "rotate(45deg)",
        note: "The initial above it stays unrotated.",
    },
    {
        partId: "fill",
        property: "background-color",
        token: "--color-ocean-blue-200",
        via: "--avatar-bg",
        note: "Through --avatar-color, which a `color` prop (a class's iconColor) replaces inline.",
    },
];

const shapeSpecs: ReadonlyArray<{
    shape: AvatarShape;
    restingBindings: readonly AnatomyBinding[];
    sizeBindings: SizeBindings;
}> = [
    {
        shape: "circle",
        restingBindings: [{ ...radiusBinding, literal: "50%" }],
        sizeBindings: avatarSeamBindings.circle,
    },
    {
        shape: "square",
        restingBindings: [
            {
                ...radiusBinding,
                token: "--radius-xs",
                note: "4px at every size: Figma's EntityIcon corner, shared by firm logos (spec B1).",
            },
        ],
        sizeBindings: withEntityLetter(avatarSeamBindings.square),
    },
    {
        shape: "diamond",
        restingBindings: diamondBindings,
        sizeBindings: withEntityLetter(avatarSeamBindings.diamond),
    },
];

const variants: readonly AnatomyVariant[] = shapeSpecs.flatMap(
    ({ shape, restingBindings, sizeBindings }) =>
        avatarPaints
            .filter((paint) => paint.shape === shape)
            .map(({ variant, tone }) => ({
                id: avatarAnatomyVariantId({ shape, variant, tone }),
                label: `${shape} ${variant} ${tone}`,
                selector: `[data-shape="${shape}"][data-variant="${variant}"][data-tone="${tone}"]`,
                restingBindings: [
                    ...restingBindings,
                    ...avatarPaintBindings({ shape, variant, tone }),
                ],
                stateBindings: {},
                sizeBindings,
            })),
);

export const avatarAnatomy: ComponentAnatomySpec = {
    label: "Avatar",
    schematic: "avatar",
    rootSelector: ".avatar",
    variantAxisLabel: "Shape",
    parts,
    states,
    sharedBindings,
    variants,
    sizes: avatarAnatomySizes,
};
